import {
  DEFAULT_RENT_SORT,
  DocumentType,
  RentCellStatus,
  RentExportLayout,
  RentGroupBy,
  RentRowKind,
  RentSortField,
  RentValueMode,
  SortDirection,
  type RentSort,
} from '@rentguru/commons-utils';
import { RentCellTone } from '@up2rent/ui-new';

import { type ReminderDocumentType } from './rentTrackingUtils';

export type RentTrackingEmptyStateKind = 'empty' | 'error';

interface RentTrackingEmptyStateMessageIds {
  body: string;
  title: string;
}

export const EMPTY_STATE_MESSAGE_IDS: Record<RentTrackingEmptyStateKind, RentTrackingEmptyStateMessageIds> = {
  empty: { body: 'dashboard.widgets.rent.empty.body', title: 'dashboard.widgets.rent.empty.title' },
  error: { body: 'dashboard.widgets.rent.error.body', title: 'dashboard.widgets.rent.error.title' },
};

interface StatusFilterOption {
  labelId: string;
  status: RentCellStatus;
}

export const STATUS_FILTER_OPTIONS: StatusFilterOption[] = [
  { labelId: 'dashboard.widgets.rent.filter.hasLate', status: RentCellStatus.LATE },
  { labelId: 'dashboard.widgets.rent.filter.hasPartial', status: RentCellStatus.PARTIAL },
  { labelId: 'dashboard.widgets.rent.filter.hasVacant', status: RentCellStatus.VACANT },
  { labelId: 'dashboard.widgets.rent.filter.hasEmpty', status: RentCellStatus.EMPTY },
];

export const GROUP_BY_LABEL_IDS: Record<RentGroupBy, string> = {
  [RentGroupBy.BUILDING]: 'dashboard.widgets.rent.filter.building',
  [RentGroupBy.OWNER]: 'dashboard.widgets.rent.filter.owner',
};

export const NO_GROUP_LABEL_IDS: Record<RentGroupBy, string> = {
  [RentGroupBy.BUILDING]: 'dashboard.widgets.rent.groupBy.noBuilding',
  [RentGroupBy.OWNER]: 'dashboard.widgets.rent.groupBy.noOwner',
};

export const STATUS_LABEL_IDS: Record<RentCellStatus, string> = {
  [RentCellStatus.COVERED]: 'dashboard.widgets.rent.legend.covered',
  [RentCellStatus.EMPTY]: 'dashboard.widgets.rent.legend.empty',
  [RentCellStatus.FUTURE]: 'dashboard.widgets.rent.legend.future',
  [RentCellStatus.LATE]: 'dashboard.widgets.rent.legend.late',
  [RentCellStatus.NONE]: 'dashboard.widgets.rent.legend.uncalled',
  [RentCellStatus.PAID]: 'dashboard.widgets.rent.legend.paid',
  [RentCellStatus.PARTIAL]: 'dashboard.widgets.rent.legend.partial',
  [RentCellStatus.VACANT]: 'dashboard.widgets.rent.legend.vacant',
};

interface RentLegendMessageIds {
  descriptionId: string;
  labelId: string;
}

export const LEGEND_MESSAGE_IDS: Record<RentCellTone, RentLegendMessageIds> = {
  [RentCellTone.CALLED_NOT_DUE]: {
    descriptionId: 'dashboard.widgets.rent.legendHelp.called',
    labelId: 'dashboard.widgets.rent.legend.called',
  },
  [RentCellTone.COVERED]: {
    descriptionId: 'dashboard.widgets.rent.legendHelp.covered',
    labelId: 'dashboard.widgets.rent.legend.covered',
  },
  [RentCellTone.EMPTY]: {
    descriptionId: 'dashboard.widgets.rent.legendHelp.empty',
    labelId: 'dashboard.widgets.rent.legend.empty',
  },
  [RentCellTone.FORECAST]: {
    descriptionId: 'dashboard.widgets.rent.legendHelp.future',
    labelId: 'dashboard.widgets.rent.legend.future',
  },
  [RentCellTone.LATE]: {
    descriptionId: 'dashboard.widgets.rent.legendHelp.late',
    labelId: 'dashboard.widgets.rent.legend.late',
  },
  [RentCellTone.NONE]: {
    descriptionId: 'dashboard.widgets.rent.legendHelp.uncalled',
    labelId: 'dashboard.widgets.rent.legend.uncalled',
  },
  [RentCellTone.PAID]: {
    descriptionId: 'dashboard.widgets.rent.legendHelp.paid',
    labelId: 'dashboard.widgets.rent.legend.paid',
  },
  [RentCellTone.PARTIAL]: {
    descriptionId: 'dashboard.widgets.rent.legendHelp.partial',
    labelId: 'dashboard.widgets.rent.legend.partial',
  },
  [RentCellTone.VACANT]: {
    descriptionId: 'dashboard.widgets.rent.legendHelp.vacant',
    labelId: 'dashboard.widgets.rent.legend.vacant',
  },
};

export const MODE_LABEL_IDS: Record<RentValueMode, string> = {
  [RentValueMode.COLLECTED]: 'dashboard.widgets.rent.mode.collected',
  [RentValueMode.EXPECTED]: 'dashboard.widgets.rent.mode.expected',
  [RentValueMode.OUTSTANDING]: 'dashboard.widgets.rent.mode.outstanding',
};

export const RENT_VALUE_MODES: RentValueMode[] = [
  RentValueMode.COLLECTED,
  RentValueMode.OUTSTANDING,
  RentValueMode.EXPECTED,
];

export const RENT_PAGE_DEFAULT_SORT: RentSort = { direction: SortDirection.ASC, field: RentSortField.TENANT_UNIT };

export const RENT_UNIT_VIEW_DEFAULT_SORT: RentSort = { direction: SortDirection.ASC, field: RentSortField.UNIT };

export const RENT_DEFAULT_SORT_BY_ROW_VIEW: Record<RentRowKind, RentSort> = {
  [RentRowKind.LEASE]: RENT_PAGE_DEFAULT_SORT,
  [RentRowKind.UNIT]: RENT_UNIT_VIEW_DEFAULT_SORT,
};

export const RENT_PAGE_DEFAULT_ROW_VIEW = RentRowKind.UNIT;

export const RENT_ROW_VIEWS: RentRowKind[] = [RentRowKind.UNIT, RentRowKind.LEASE];

export const ROW_VIEW_LABEL_IDS: Record<RentRowKind, string> = {
  [RentRowKind.LEASE]: 'dashboard.widgets.rent.rowView.lease',
  [RentRowKind.UNIT]: 'dashboard.widgets.rent.rowView.unit',
};

export const NAME_SORT_FIELD_BY_ROW_VIEW: Record<RentRowKind, RentSortField> = {
  [RentRowKind.LEASE]: RentSortField.TENANT_UNIT,
  [RentRowKind.UNIT]: RentSortField.UNIT,
};

export const NAME_COLUMN_LABEL_IDS: Record<RentRowKind, string> = {
  [RentRowKind.LEASE]: 'dashboard.widgets.rent.tenantUnit',
  [RentRowKind.UNIT]: 'dashboard.widgets.rent.unitColumn',
};

export interface RentSortOption {
  labelId: string;
  sort: RentSort;
}

export const RENT_SORT_OPTIONS: RentSortOption[] = [
  { labelId: 'dashboard.widgets.rent.unitColumn', sort: RENT_UNIT_VIEW_DEFAULT_SORT },
  { labelId: 'dashboard.widgets.rent.tenantUnit', sort: RENT_PAGE_DEFAULT_SORT },
  {
    labelId: 'dashboard.widgets.rent.sort.leaseRef',
    sort: { direction: SortDirection.ASC, field: RentSortField.LEASE_REF },
  },
  { labelId: 'dashboard.widgets.rent.sort.unpaidFirst', sort: DEFAULT_RENT_SORT },
];

export interface RentExportLayoutOption {
  hintId: string;
  labelId: string;
  layout: RentExportLayout;
}

export const RENT_EXPORT_LAYOUT_OPTIONS: RentExportLayoutOption[] = [
  {
    hintId: 'dashboard.widgets.rent.exportLayouts.matrix.hint',
    labelId: 'dashboard.widgets.rent.exportLayouts.matrix.label',
    layout: RentExportLayout.MATRIX,
  },
  {
    hintId: 'dashboard.widgets.rent.exportLayouts.byCategory.hint',
    labelId: 'dashboard.widgets.rent.exportLayouts.byCategory.label',
    layout: RentExportLayout.MATRIX_BY_CATEGORY,
  },
  {
    hintId: 'dashboard.widgets.rent.exportLayouts.ownerSummary.hint',
    labelId: 'dashboard.widgets.rent.exportLayouts.ownerSummary.label',
    layout: RentExportLayout.OWNER_SUMMARY,
  },
];

export const REMINDER_LABEL_IDS: Record<ReminderDocumentType, string> = {
  [DocumentType.LeaseFirstReminder]: 'lease.detail.action.generateFirstReminder',
  [DocumentType.LeaseSecondReminder]: 'lease.detail.action.generateSecondReminder',
  [DocumentType.LeaseThirdReminder]: 'lease.detail.action.generateThirdReminder',
  [DocumentType.LeaseFormalNotice]: 'lease.detail.action.generateFormalNotice',
};
